import type { z } from '@uturn/portal/zod';

export const RequiredField = 'required';

/**
 * Get required fields from a Zod Schema.
 *
 * @param schema Zod Schema
 * @returns Required fields key-value pairs where key is the field name and value is true or false.
 *
 * @usage
 * Modify any form zod schema and add the following `.describe(RequiredField)` to any mandatory fields,
 * as follows:
 * ```ts
 *   email: z.string().email().describe(RequiredField)
 * ```
 * Then call the function as follows:
 * ```ts
 * const requiredField = getRequiredFields(formInviteUserSchema);
 * ```
 *
 * @example
 * formInviteUserSchema in 'apps/portal/src/modules/org/schema/invite-user.ts'.
 */
export const getRequiredFields = (
	schema: z.ZodObject<z.ZodRawShape>,
): Record<string, boolean> => {
	const requiredFields: Record<string, boolean> = {};

	const processSchema = (shape: z.ZodRawShape, prefix = '') => {
		for (const [key, value] of Object.entries(shape)) {
			const fullKey = prefix ? `${prefix}.${key}` : key;
			const zodType = value as z.ZodTypeAny;
			const { description } = zodType._def;

			// Check if this field is required
			requiredFields[fullKey] =
				(description && description === RequiredField) ?? false;

			// If the field is a ZodObject, recursively process its shape
			if (zodType._def?.typeName === 'ZodObject') {
				const zodObject = zodType as z.ZodObject<z.ZodRawShape>;
				processSchema(zodObject.shape, fullKey);
			}
		}
	};

	processSchema(schema.shape);

	return requiredFields;
};
